/**
 * The application shell.
 *
 * Four things happen here and nothing else: the browser is probed once, the live session
 * is mounted, the router is mounted, and the build badge is hung in the corner of the
 * viewport where every screen carries it. `SessionHost` owns the input sources — the
 * microphone and the recogniser at the piano, the selection funnel On the Go — and puts a
 * fully wired `SessionView` in context; `useSessionView` still falls back to an idle
 * session anywhere the host is absent, which is what keeps `/dev/states` and the component
 * tests renderable from fixtures alone.
 *
 * The host sits *above* the routes rather than inside `SessionPage`, so navigating to the
 * sketchbook and back does not restart the session or reacquire the microphone.
 *
 * The capability probe runs once, at module scope in effect, rather than per render:
 * `AudioWorkletNode` does not appear halfway through a session, and re-probing on every
 * render would make the unsupported screen flicker.
 */

import { useCallback, useMemo } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { probeCapabilities } from './capabilities.js';
import { AppRoutes } from './routes.js';
import { BuildBadge } from './components/system/BuildBadge.js';
import {
  SessionProvider,
  createIdleSessionView,
  useSessionView,
} from './components/system/sessionView.js';
import { SessionHost } from './state/sessionHost.js';
import './styles/index.css';

/**
 * Injects the capability report into whatever view is in scope.
 *
 * Written as a component rather than folded into `App` so that the probe result reaches
 * both the store's view (once there is one) and the idle fallback, without either of them
 * having to know about `capabilities.ts`.
 */
function CapabilityAwareRoutes(): React.JSX.Element {
  const existing = useSessionView();
  const capabilities = useMemo(() => probeCapabilities(), []);
  const view = useMemo(
    () => ({ ...existing, capabilities: existing.capabilities ?? capabilities }),
    [existing, capabilities],
  );
  return (
    <SessionProvider value={view}>
      <AppRoutes />
    </SessionProvider>
  );
}

/**
 * The live session, inside the router.
 *
 * `useNavigate` is read here rather than in the host so that `SessionHost` itself has no
 * router dependency and can be mounted bare in a test. Routing is the only thing the host
 * cannot do for itself: "view the sketch you just saved" is a navigation, not a session
 * action.
 */
function LiveSession(): React.JSX.Element {
  const navigate = useNavigate();
  const goTo = useCallback(
    (to: string) => {
      void navigate(to);
    },
    [navigate],
  );
  return (
    <SessionHost navigate={goTo}>
      <CapabilityAwareRoutes />
    </SessionHost>
  );
}

export function App(): React.JSX.Element {
  return (
    <>
      <BrowserRouter>
        <LiveSession />
      </BrowserRouter>
      {/*
        Outside the router on purpose, and outside `AppFrame` too. Which build is running is
        not a property of a route, and the badge is fixed to the corner of the viewport
        rather than to a screen — so this is the only place that reaches every screen without
        being mounted once per route. It reads `--hc-fs-build`, which is the one type token
        that does not scale with the viewport and so does not need the frame to publish it.
      */}
      <BuildBadge />
    </>
  );
}

export { createIdleSessionView };
export default App;
